"use client"

import { useState } from "react"
import { LexicalText } from "@/schema"
import { Button } from "@ui/shad"
import { Expand } from "lucide-react"

import { useSyncboxEmailDraft } from "@/hooks/api/syncbox_email_drafts"
import { CustomDialog } from "@/components/dialogs"
import { Editor } from "@/components/lexical"

import { ComposeEmail } from "./compose"

interface ComposeEmailPreviewProps {
  syncboxDraftId?: string
  onTaskComplete?: () => void
}

export function ComposeEmailPreview({ syncboxDraftId, onTaskComplete }: ComposeEmailPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: syncboxDraft, isLoading: isLoadingDraft } = useSyncboxEmailDraft(syncboxDraftId || "")

  const handleEmailSent = () => {
    setIsExpanded(false)
    onTaskComplete?.()
  }

  const handleDialogClose = () => {
    setIsExpanded(false)
  }

  if (!syncboxDraftId) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground text-sm">Message unavailable</p>
      </div>
    )
  }

  if (isLoadingDraft) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading email...</p>
      </div>
    )
  }

  if (!syncboxDraft) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground text-sm">Message unavailable</p>
      </div>
    )
  }

  return (
    <>
      <div className="relative p-4">
        <div className="bg-muted/50 absolute inset-0 z-10 flex items-start justify-end rounded-lg p-4">
          <Button type="button" onClick={() => setIsExpanded(true)}>
            <Expand />
            Expand
          </Button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-1">
            {syncboxDraft.to_recipients && syncboxDraft.to_recipients.length > 0 && (
              <p className="text-sm">
                <span className="font-medium">To:</span> {syncboxDraft.to_recipients.map((r) => r.email).join(", ")}
              </p>
            )}
            {syncboxDraft.subject && (
              <p className="text-sm">
                <span className="font-medium">Subject:</span> {syncboxDraft.subject}
              </p>
            )}
          </div>
        </div>

        <div className="mt-2 max-h-[200px] overflow-hidden rounded-md border">
          <div className="p-4">
            {syncboxDraft.body && (
              <Editor
                initialContent={
                  { html: syncboxDraft.body } as LexicalText // Create LexicalText from body HTML if content doesn't exist
                }
                editable={false}
                styleOverrides={{
                  "--lexical-editor-font-size": "14px",
                }}
              />
            )}
          </div>
        </div>
      </div>

      <CustomDialog
        open={isExpanded}
        onOpenChange={setIsExpanded}
        title="Compose email"
        overlayClassName="bg-background/80 backdrop-blur-sm"
        customClassName="top-[50%] left-[50%] flex h-[70vh] max-h-[60rem] w-[90vw] max-w-3xl translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg p-0 shadow-xl"
      >
        <ComposeEmail
          syncboxDraftId={syncboxDraftId}
          onSent={handleEmailSent}
          onClose={handleDialogClose}
          hidePinning
        />
      </CustomDialog>
    </>
  )
}
